import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Plus, Edit2, Trash2, X, Calendar, Users, Megaphone, AlertTriangle, Search, SortAsc, SortDesc, ChevronDown, Filter } from 'lucide-react';
import Pagination from '../components/Pagination';

function getCampaignStatus(c) {
  const now = new Date();
  const start = c.startDate ? new Date(c.startDate) : null;
  const end = c.endDate ? new Date(c.endDate) : null;
  if (!c.isActive) return 'inactive';
  if (start && now < start) return 'scheduled';
  if (end && now > end) return 'expired';
  return 'active';
}

function StatusBadge({ campaign }) {
  const status = campaign.status || getCampaignStatus(campaign);
  const map = {
    active: { label: 'Active', bg: '#DCFCE7', color: '#166534', icon: '●' },
    scheduled: { label: 'Scheduled', bg: '#EDE9FE', color: '#5B21B6', icon: '◷' },
    expired: { label: 'Expired', bg: '#FEE2E2', color: '#991B1B', icon: '✕' },
    inactive: { label: 'Inactive', bg: '#F3F4F6', color: '#6B7280', icon: '○' },
  };
  const s = map[status] || map.inactive;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 10px', borderRadius: 20, fontSize: '0.7rem', fontWeight: 600, background: s.bg, color: s.color }}>
      {s.icon} {s.label}
    </span>
  );
}

// ─── Campaign Form ────────────────────────────────────────────────────────────
function CampaignForm({ campaign, doctors, templates, allCampaigns, onSave, onClose }) {
  const [form, setForm] = useState({
    name: campaign?.name || '', description: campaign?.description || '',
    templateId: campaign?.templateId?._id || campaign?.templateId || '',
    doctors: campaign?.doctors?.map(d => d._id || d) || [],
    startDate: campaign?.startDate ? campaign.startDate.split('T')[0] : '',
    endDate: campaign?.endDate ? campaign.endDate.split('T')[0] : '',
    isActive: campaign?.isActive !== false
  });
  const [loading, setLoading] = useState(false);

  const conflictMap = {};
  allCampaigns.forEach(c => {
    if ((c.status || getCampaignStatus(c)) !== 'active') return;
    if (campaign?._id && c._id === campaign._id) return;
    (c.doctors || []).forEach(d => {
      const id = d._id || d;
      if (!conflictMap[id]) conflictMap[id] = [];
      conflictMap[id].push(c.name);
    });
  });

  const toggleDoctor = (id) => setForm(p => ({ ...p, doctors: p.doctors.includes(id) ? p.doctors.filter(d => d !== id) : [...p.doctors, id] }));
  const conflictingSelected = form.doctors.filter(id => conflictMap[id]);

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      if (campaign?._id) { await axios.put(`/api/campaigns/${campaign._id}`, form); toast.success('Campaign updated!'); }
      else { await axios.post('/api/campaigns', form); toast.success('Campaign created!'); }
      onSave();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 600 }}>
        <div className="modal-header">
          <h2>{campaign ? 'Edit Campaign' : 'New Campaign'}</h2>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group"><label className="form-label">Campaign Name *</label><input className="form-input" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Epilepsy Day 2026" required /></div>
          <div className="form-group"><label className="form-label">Description</label><textarea className="form-input" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={2} /></div>
          <div className="form-group">
            <label className="form-label">Poster Template *</label>
            <select className="form-input" value={form.templateId} onChange={e => setForm(p => ({ ...p, templateId: e.target.value }))} required>
              <option value="">Select template...</option>
              {templates.map(t => <option key={t._id} value={t._id}>{t.name} {t.mode === 'image' ? '🖼' : '🎨'}</option>)}
            </select>
          </div>
          <div className="grid-2">
            <div className="form-group"><label className="form-label">Start Date</label><input className="form-input" type="date" value={form.startDate} onChange={e => setForm(p => ({ ...p, startDate: e.target.value }))} /></div>
            <div className="form-group"><label className="form-label">End Date</label><input className="form-input" type="date" value={form.endDate} onChange={e => setForm(p => ({ ...p, endDate: e.target.value }))} /></div>
          </div>

          {conflictingSelected.length > 0 && (
            <div style={{ background: '#FEF9C3', border: '1.5px solid #FCD34D', borderRadius: 10, padding: '10px 14px', marginBottom: 14, display: 'flex', gap: 10 }}>
              <AlertTriangle size={17} color="#92400E" style={{ flexShrink: 0, marginTop: 1 }} />
              <div>
                <div style={{ fontWeight: 700, fontSize: '0.8rem', color: '#78350F', marginBottom: 4 }}>{conflictingSelected.length} doctor(s) in another active campaign</div>
                {conflictingSelected.map(id => { const doc = doctors.find(d => d._id === id); return <div key={id} style={{ fontSize: '0.76rem', color: '#92400E' }}>• <strong>{doc?.name}</strong> → {conflictMap[id].join(', ')}</div>; })}
                <div style={{ fontSize: '0.72rem', color: '#92400E', marginTop: 4, fontStyle: 'italic' }}>You can still save — but profile will show a random poster.</div>
              </div>
            </div>
          )}

          <div className="form-group">
            {/* <label className="form-label">Assign Doctors ({form.doctors.length} selected)</label> */}
        <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
  <input
    type="checkbox"
    checked={doctors.length > 0 && form.doctors.length === doctors.length}
    ref={el => { if (el) el.indeterminate = form.doctors.length > 0 && form.doctors.length < doctors.length; }}
    onChange={e => setForm(p => ({ ...p, doctors: e.target.checked ? doctors.map(d => d._id) : [] }))}
    style={{ accentColor: 'var(--purple-800)', width: 15, height: 15 }}
  />
  Assign Doctors ({form.doctors.length} selected)
</label>
            <div style={{ border: '1.5px solid var(--gray-200)', borderRadius: 10, maxHeight: 220, overflowY: 'auto', padding: 8 }}>
              {doctors.length === 0
                ? <div style={{ padding: 12, color: 'var(--gray-400)', fontSize: '0.85rem', textAlign: 'center' }}>No doctors available</div>
                : doctors.map(doc => {
                  const conflict = conflictMap[doc._id];
                  const checked = form.doctors.includes(doc._id);
                  return (
                    <label key={doc._id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 8px', cursor: 'pointer', borderRadius: 7, background: conflict && checked ? '#FEF9C3' : 'transparent', marginBottom: 2 }}>
                      <input type="checkbox" checked={checked} onChange={() => toggleDoctor(doc._id)} style={{ accentColor: 'var(--purple-800)', width: 15, height: 15, flexShrink: 0 }} />
                      <div style={{ flex: 1 }}><div style={{ fontSize: '0.875rem', fontWeight: 500 }}>{doc.name}</div>{(doc.phone ||doc.specialty) && <div style={{ fontSize: '0.72rem', color: 'var(--gray-400)' }}>{doc.specialty} {doc.phone ?` | ${doc.phone}`:''}</div>}</div>
                      {conflict && <span style={{ display: 'flex', alignItems: 'center', gap: 3, background: '#FCD34D', borderRadius: 5, padding: '1px 6px', fontSize: '0.66rem', color: '#78350F', fontWeight: 600, flexShrink: 0 }}><AlertTriangle size={10} />In: {conflict.join(', ')}</span>}
                    </label>
                  );
                })
              }
            </div>
          </div>

          <div style={{ background: 'var(--gray-50)', border: '1px solid var(--gray-200)', borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: '0.76rem', color: 'var(--gray-600)', lineHeight: 1.8 }}>
            <strong style={{ fontSize: '0.78rem' }}>Status rules:</strong>{' '}
            <span style={{ background: '#DCFCE7', color: '#166534', borderRadius: 4, padding: '0 5px', fontWeight: 600 }}>● Active</span> — dates valid &nbsp;
            <span style={{ background: '#EDE9FE', color: '#5B21B6', borderRadius: 4, padding: '0 5px', fontWeight: 600 }}>◷ Scheduled</span> — future start &nbsp;
            <span style={{ background: '#FEE2E2', color: '#991B1B', borderRadius: 4, padding: '0 5px', fontWeight: 600 }}>✕ Expired</span> — auto-disabled after end date
          </div>

          <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <input type="checkbox" id="isActive" checked={form.isActive} onChange={e => setForm(p => ({ ...p, isActive: e.target.checked }))} style={{ accentColor: 'var(--purple-800)', width: 15, height: 15 }} />
            <label htmlFor="isActive" style={{ fontSize: '0.875rem', cursor: 'pointer', fontWeight: 500 }}>Enabled <span style={{ fontWeight: 400, color: 'var(--gray-400)' }}>(uncheck to manually pause)</span></label>
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? <><div className="spinner" />&nbsp;Saving...</> : 'Save Campaign'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Main Campaigns Page ──────────────────────────────────────────────────────
export default function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [allDoctors, setAllDoctors] = useState([]);
  const [allTemplates, setAllTemplates] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [sort, setSort] = useState('createdAt');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const searchTimer = useRef(null);

  // Fetch paginated campaigns + all doctors/templates for form (unpaginated)
  const fetchCampaigns = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const p = { page, limit, search, sort, order, status, ...params };
      const qs = new URLSearchParams();
      Object.entries(p).forEach(([k, v]) => { if (v !== '' && v != null) qs.set(k, v); });
      const res = await axios.get(`/api/campaigns?${qs.toString()}`);
      setCampaigns(res.data.data);
      setPagination(res.data.pagination);
    } catch { toast.error('Failed to load campaigns'); }
    finally { setLoading(false); }
  }, [page, limit, search, sort, order, status]);

  const fetchFormData = useCallback(async () => {
    try {
      const [d, t] = await Promise.all([
        axios.get('/api/doctors?pagination=false&fields=_id,name,phone,specialty'),
        axios.get('/api/templates?pagination=false&fields=_id,name')
      ]);
      setAllDoctors(d.data.data);
      setAllTemplates(t.data.data);
    } catch { }
  }, []);

  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);
  useEffect(() => { fetchFormData(); }, [fetchFormData]);

  const handleSearch = (val) => {
    setSearch(val); setPage(1);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => fetchCampaigns({ search: val, page: 1 }), 400);
  };

  const handleSort = (field, newOrder) => { setSort(field); setOrder(newOrder); setPage(1); fetchCampaigns({ sort: field, order: newOrder, page: 1 }); };
  const handleStatus = (val) => { setStatus(val); setPage(1); fetchCampaigns({ status: val, page: 1 }); };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this campaign?')) return;
    try { await axios.delete(`/api/campaigns/${id}`); toast.success('Deleted'); fetchCampaigns(); }
    catch { toast.error('Delete failed'); }
  };

  const activeFilters = [search, status].filter(Boolean).length;

  // Conflict detection (only active campaigns)
  const activeDoctorCount = {};
  campaigns.filter(c => (c.status || getCampaignStatus(c)) === 'active').forEach(c => {
    (c.doctors || []).forEach(d => { const id = d._id || d; activeDoctorCount[id] = (activeDoctorCount[id] || 0) + 1; });
  });
  const hasConflicts = Object.values(activeDoctorCount).some(v => v > 1);

  return (
    <div>
      <div className="page-header">
        <div><h1>Campaigns</h1><p>{pagination.total} campaigns total</p></div>
        <button className="btn btn-primary" onClick={() => { setEditing(null); setShowForm(true); }}><Plus size={16} /> New Campaign</button>
      </div>

      {hasConflicts && (
        <div style={{ background: '#FEF9C3', border: '1.5px solid #FCD34D', borderRadius: 12, padding: '10px 16px', marginBottom: 18, display: 'flex', gap: 10, alignItems: 'center', fontSize: '0.8rem', color: '#78350F' }}>
          <AlertTriangle size={16} color="#92400E" style={{ flexShrink: 0 }} />
          <strong>Conflict detected:</strong>&nbsp;Some doctors are in multiple active campaigns — their poster will be random. Edit campaigns to resolve.
        </div>
      )}

      {/* ── Toolbar ── */}
      <div className="card" style={{ marginBottom: 20, padding: '14px 18px' }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: '1 1 200px', minWidth: 160 }}>
            <Search size={15} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-400)' }} />
            <input className="form-input" placeholder="Search campaigns..." value={search} onChange={e => handleSearch(e.target.value)} style={{ paddingLeft: 34, margin: 0 }} />
          </div>

          {/* Status filter */}
          <div style={{ position: 'relative' }}>
            <Filter size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: status ? 'var(--purple-800)' : 'var(--gray-400)', pointerEvents: 'none' }} />
            <select value={status} onChange={e => handleStatus(e.target.value)}
              style={{ paddingLeft: 28, paddingRight: 28, height: 38, borderRadius: 10, border: `1.5px solid ${status ? 'var(--purple-400)' : 'var(--gray-200)'}`, fontFamily: 'DM Sans, sans-serif', fontSize: '0.82rem', cursor: 'pointer', outline: 'none', color: status ? 'var(--purple-800)' : 'var(--gray-600)', background: status ? 'var(--purple-100)' : 'white', appearance: 'none' }}>
              <option value="">All Status</option>
              <option value="active">● Active</option>
              <option value="scheduled">◷ Scheduled</option>
              <option value="expired">✕ Expired</option>
              <option value="inactive">○ Inactive</option>
            </select>
            <ChevronDown size={13} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--gray-400)' }} />
          </div>

          {/* Sort */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span style={{ fontSize: '0.75rem', color: 'var(--gray-400)', whiteSpace: 'nowrap' }}>Sort:</span>
            {[['name', 'Name'], ['createdAt', 'Created'], ['startDate', 'Start'], ['endDate', 'End']].map(([f, l]) => (
              <button key={f} onClick={() => handleSort(f, sort === f && order === 'desc' ? 'asc' : 'desc')}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 7, border: `1.5px solid ${sort === f ? 'var(--purple-400)' : 'var(--gray-200)'}`, background: sort === f ? 'var(--purple-100)' : 'white', color: sort === f ? 'var(--purple-800)' : 'var(--gray-600)', fontSize: '0.78rem', fontWeight: sort === f ? 600 : 400, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>
                {l}{sort === f ? (order === 'asc' ? <SortAsc size={12} /> : <SortDesc size={12} />) : <SortAsc size={12} style={{ opacity: 0.3 }} />}
              </button>
            ))}
          </div>

          {activeFilters > 0 && (
            <button className="btn btn-sm btn-outline" onClick={() => { setSearch(''); setStatus(''); setSort('createdAt'); setOrder('desc'); setPage(1); fetchCampaigns({ search: '', status: '', sort: 'createdAt', order: 'desc', page: 1 }); }}>
              <X size={13} /> Clear ({activeFilters})
            </button>
          )}
        </div>
      </div>

      {/* ── List ── */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><div className="spinner" style={{ width: 36, height: 36, borderColor: 'rgba(107,33,168,0.2)', borderTopColor: 'var(--purple-800)' }} /></div>
      ) : (
        <>
          {campaigns.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: 60 }}>
              <Megaphone size={40} style={{ margin: '0 auto 12px', opacity: 0.3 }} />
              <h3 style={{ marginBottom: 8 }}>No campaigns found</h3>
              <p style={{ color: 'var(--gray-400)', marginBottom: 20 }}>{search || status ? 'Try adjusting your filters' : 'Create your first campaign'}</p>
              {!search && !status && <button className="btn btn-primary" onClick={() => setShowForm(true)}><Plus size={16} /> New Campaign</button>}
            </div>
          ) : (
            <>

              <Pagination
                page={pagination.page} limit={pagination.limit} total={pagination.total}
                onPageChange={p => { setPage(p); fetchCampaigns({ page: p }); }}
                onLimitChange={l => { setLimit(l); setPage(1); fetchCampaigns({ limit: l, page: 1 }); }}
              />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {campaigns.map(c => {
                  const conflictDocs = (c.doctors || []).filter(d => activeDoctorCount[d._id || d] > 1);
                  const st = c.status || getCampaignStatus(c);
                  return (
                    <div className="card" key={c._id} style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '14px 18px' }}>
                      <div style={{ width: 44, height: 44, background: st === 'active' ? 'var(--purple-100)' : st === 'scheduled' ? '#EDE9FE' : 'var(--gray-100)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
                        <Megaphone size={20} color={st === 'active' ? 'var(--purple-800)' : st === 'scheduled' ? '#7C3AED' : 'var(--gray-400)'} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                          <div style={{ fontWeight: 700, fontSize: '0.92rem' }}>{c.name}</div>
                          <StatusBadge campaign={c} />
                          {conflictDocs.length > 0 && st === 'active' && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 3, background: '#FCD34D', borderRadius: 6, padding: '2px 7px', fontSize: '0.68rem', color: '#78350F', fontWeight: 600 }}>
                              <AlertTriangle size={10} /> {conflictDocs.length} conflict{conflictDocs.length > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems:'center',flexWrap: 'wrap', gap: 12, fontSize: '0.76rem', color: 'var(--gray-400)'  }}>
                          {c.templateId && <span>📋 {c.templateId.name}</span>}
                          <span><Users size={11} style={{ display: 'inline', marginRight: 3 }} />{c.doctors?.length || 0} doctors</span>
                          {c.startDate && <span><Calendar size={11} style={{ display: 'inline', marginRight: 3 }} />{new Date(c.startDate).toLocaleDateString()} – {c.endDate ? new Date(c.endDate).toLocaleDateString() : 'ongoing'}</span>}
                        </div>
                        {c.description && <div style={{ fontSize: '0.78rem', color: 'var(--gray-500)', marginTop: 4 }}>{c.description}</div>}
                        {conflictDocs.length > 0 && st === 'active' && (
                          <div style={{ fontSize: '0.72rem', color: '#92400E', marginTop: 5, background: '#FEF9C3', borderRadius: 5, padding: '3px 8px', display: 'inline-block' }}>
                            ⚠ {conflictDocs.map(d => d.name || d).join(', ')}
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                        <button className="btn btn-sm btn-outline" onClick={() => { setEditing(c); setShowForm(true); }}><Edit2 size={13} /></button>
                        <button className="btn btn-sm btn-danger" onClick={() => handleDelete(c._id)}><Trash2 size={13} /></button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}

      {showForm && (
        <CampaignForm campaign={editing} doctors={allDoctors} templates={allTemplates} allCampaigns={campaigns}
          onSave={() => { setShowForm(false); fetchCampaigns(); fetchFormData(); }}
          onClose={() => setShowForm(false)} />
      )}
    </div>
  );
}
